/**
 * What has shipped on CHU-GPT, written for the people who use it.
 *
 * WHY THIS IS A HAND-WRITTEN LIST AND NOT GENERATED FROM GIT.
 *
 * Three reasons, each of which would be enough on its own.
 *
 * VOLUME. The repository holds hundreds of commits for a platform that is
 * weeks old, and most of them touch application code. Even a perfect filter
 * leaves an order of magnitude more entries than anyone would read.
 *
 * GRAIN. A commit answers "what did the author change". A reader here is
 * asking "what can I do now that I could not do before". Those are different
 * questions and the first does not imply the second: a change that renames a
 * variable and a change that adds a new tool look identical from the outside.
 *
 * SAFETY, and this is the one that settles it. Commit SUBJECTS in this
 * repository leak on their own, before anyone opens a body. Real examples
 * from this history name a security remediation, a production outage and a
 * feature kill switch. Filtering that with a deny-list is the wrong shape:
 * any list of forbidden words is one commit away from being wrong, and the
 * failure is silent and published to every member of staff. An allow-list
 * where a human writes each sentence cannot fail that way, because nothing
 * reaches the page unless somebody wrote it for the page.
 *
 * So: `scripts/build-log-draft.sh` PROPOSES candidates on a terminal, a human
 * writes the entry, and `test/buildLog.test.ts` fails the build when this file
 * falls behind what is deployed. The generator has no path to this file.
 *
 * HOUSE RULES FOR AN ENTRY.
 *   - Say what a person can now do. Not what was built, not how.
 *   - No internal service names, no schema names, no file paths.
 *   - No incidents, outages, security findings or remediation.
 *   - No business figures, costs, or counts taken from the warehouse.
 *   - A spec is not a release. If it did not ship, it is not here.
 */

/**
 * Where a person meets this change.
 *
 * Deliberately the PRODUCT's shape and not the repository's: a reader knows
 * "the chat" and "Teams", and has no reason to know which container serves
 * them.
 */
export type Surface = 'chat' | 'agents' | 'tools' | 'knowledge' | 'teams' | 'arcade'

export const SURFACE_LABEL: Record<Surface, string> = {
  chat: 'Chat',
  agents: 'Agents',
  tools: 'Tools',
  knowledge: 'Knowledge',
  teams: 'Teams',
  arcade: 'Arcade',
}

export type EntryKind = 'feature' | 'improvement'

export interface BuildLogEntry {
  /** The day it went LIVE, not the day the work started. ISO, YYYY-MM-DD. */
  readonly date: string
  /** Plain words. No version numbers, no component names. */
  readonly title: string
  /** One or two sentences: what is now possible that was not before. */
  readonly detail: string
  readonly surface: Surface
  readonly kind: EntryKind
}

/**
 * Newest first. The order is asserted by the tests rather than sorted at
 * render time, so a mis-dated entry is a failing build and not a row that
 * quietly appears in the wrong place.
 */
export const BUILD_LOG: readonly BuildLogEntry[] = [
  {
    date: '2026-09-02',
    title: 'Marketing performance, in the chat',
    detail:
      'Ask how the websites and email campaigns are tracking for CHU and Flex, and get an answer from the live numbers rather than a spreadsheet somebody exported last week. Covers site traffic and email sends, opens and clicks.',
    surface: 'tools',
    kind: 'feature',
  },
  {
    date: '2026-08-28',
    title: 'Your own profile on the board',
    detail:
      'Open anyone on the arcade to see how their usage has moved, and share a link straight to that view. Team Wars gained the same drill-down, so a faction can be opened up rather than only ranked.',
    surface: 'arcade',
    kind: 'feature',
  },
  {
    date: '2026-08-27',
    title: 'The arcade',
    detail:
      'A leaderboard for the whole platform, open to every signed-in person rather than administrators. Who is using what, which models people main, and how departments compare.',
    surface: 'arcade',
    kind: 'feature',
  },
  {
    date: '2026-08-27',
    title: 'Policy and claims administration data',
    detail:
      'Ask about a policy or a claim and get the operational record — periods, endorsements, intermediaries, reserves and transactions — without opening the administration system.',
    surface: 'tools',
    kind: 'feature',
  },
  {
    date: '2026-08-26',
    title: 'Send a query straight into the code environment',
    detail:
      'A warehouse result can now be handed directly to the code interpreter, so you can chart or model it without downloading anything and uploading it again.',
    surface: 'chat',
    kind: 'feature',
  },
  {
    date: '2026-08-25',
    title: 'Telephony data',
    detail: 'Call activity is queryable in chat alongside everything else.',
    surface: 'tools',
    kind: 'feature',
  },
  {
    date: '2026-08-23',
    title: 'The code interpreter',
    detail:
      'Python runs inside a conversation. Give it a spreadsheet and ask for the analysis, and it does the work rather than describing how you might do it.',
    surface: 'chat',
    kind: 'feature',
  },
  {
    date: '2026-08-22',
    title: 'Branded documents',
    detail:
      'Generated Word and PowerPoint files come out in CHU and Flex styling — the right typefaces, colours and furniture — so a deck needs formatting rather than rebuilding.',
    surface: 'tools',
    kind: 'improvement',
  },
  {
    date: '2026-08-21',
    title: 'Send a file to CHU GPT in Teams',
    detail:
      'Attach a spreadsheet, PDF or Word file in a Teams chat and ask about it. Images are read too. A Draft with CHU GPT action appears alongside the message box.',
    surface: 'teams',
    kind: 'feature',
  },
  {
    date: '2026-08-21',
    title: 'Pick a model or an agent inside Teams',
    detail:
      'Teams replies carry buttons for choosing a model or agent and for opening links, instead of requiring a command. It also understands being in a group chat rather than a one-to-one.',
    surface: 'teams',
    kind: 'improvement',
  },
  {
    date: '2026-08-21',
    title: 'CHU GPT in Teams',
    detail:
      'The assistant is available in Teams, signed in as you, so what you ask there is yours and counts against your own budget.',
    surface: 'teams',
    kind: 'feature',
  },
  {
    date: '2026-08-21',
    title: 'Every warehouse answer shows its working',
    detail:
      'Ask anything of the warehouse and the reply now includes the query that produced it, so a number can be checked rather than trusted.',
    surface: 'tools',
    kind: 'improvement',
  },
  {
    date: '2026-08-20',
    title: 'Edit an image you already uploaded',
    detail:
      'Drop a photo into the chat and ask for a change. Recent uploads are found automatically, so there are no addresses to copy.',
    surface: 'chat',
    kind: 'improvement',
  },
  {
    date: '2026-08-20',
    title: 'Image and video generation',
    detail: 'Describe a picture or a short video and get one back in the conversation.',
    surface: 'chat',
    kind: 'feature',
  },
  {
    date: '2026-08-20',
    title: 'Your own mail, calendar and files',
    detail:
      'Connect your Microsoft 365 account and ask about your own mail, calendar and documents. Read-only, signed in as you, and nobody else can see yours.',
    surface: 'tools',
    kind: 'feature',
  },
  {
    date: '2026-08-20',
    title: 'Finance data',
    detail:
      'Invoices, receipting, premium ledger and claim financials are queryable by the finance team, with duplicate-payment screening built in.',
    surface: 'tools',
    kind: 'feature',
  },
  {
    date: '2026-08-17',
    title: 'The underwriting manual, searchable',
    detail:
      'Ask a question of the underwriting manual and get the answer with the section and a link to it. It re-reads the manual nightly, so it does not go stale.',
    surface: 'knowledge',
    kind: 'feature',
  },
  {
    date: '2026-08-17',
    title: 'Maps of the book',
    detail:
      'Insured buildings can be drawn on a map by postcode, suburb or state, with catastrophe overlays and single-policy accumulation views. A preview appears in the chat and the full map opens in a browser.',
    surface: 'tools',
    kind: 'feature',
  },
  {
    date: '2026-08-17',
    title: 'Buildings placed at their real addresses',
    detail:
      'Mapping used to put every risk at the middle of its suburb, so a tower block was one dot. Addresses are now resolved individually, and a street of policies looks like a street.',
    surface: 'tools',
    kind: 'improvement',
  },
  {
    date: '2026-08-17',
    title: 'The underwriting workbench',
    detail:
      'Declared risk against observed data with mismatches flagged, plus accumulation checks, catastrophe exposure, referral precedents, renewal packs and an underinsurance screen.',
    surface: 'tools',
    kind: 'feature',
  },
  {
    date: '2026-08-17',
    title: 'The full claim record',
    detail:
      'One question returns a reconciled claim: causes, adjusters, builders, decline reasons, catastrophe linkage and financials, instead of several lookups stitched together by hand.',
    surface: 'tools',
    kind: 'feature',
  },
  {
    date: '2026-08-17',
    title: 'The interactive worm',
    detail:
      'The premium worm opens as a dashboard you can slice by brand, insurer, product, state and new business versus renewal, click into any point for the detail behind it, and export what you are looking at.',
    surface: 'tools',
    kind: 'feature',
  },
  {
    date: '2026-08-16',
    title: 'Premium against budget, on demand',
    detail:
      'Ask how the month is tracking and get cumulative actual against budget and last year, with product mix, state variances and the new business versus renewal split.',
    surface: 'tools',
    kind: 'feature',
  },
  {
    date: '2026-08-16',
    title: 'Policy, claims and property data in chat',
    detail:
      'Ask about the portfolio in plain language — policies, claims, building attributes and area statistics — and get an answer from the warehouse. Commercial strata property data only; no personal details.',
    surface: 'tools',
    kind: 'feature',
  },
  {
    date: '2026-08-16',
    title: 'Spreadsheets, documents and decks',
    detail:
      'Ask for an Excel, Word or PowerPoint file and get a download link. Charts in them are real, editable charts rather than pictures of charts.',
    surface: 'tools',
    kind: 'feature',
  },
  {
    date: '2026-08-14',
    title: 'A CHU sign-in page',
    detail: 'The platform looks like CHU — the logo suite, the palette, and a sign-in page to match.',
    surface: 'chat',
    kind: 'improvement',
  },
  {
    date: '2026-08-11',
    title: 'CHU GPT goes live',
    detail: 'The assistant becomes available to staff, in CHU colours, signed in with your work account.',
    surface: 'chat',
    kind: 'feature',
  },
]
